import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiUser } from 'react-icons/fi';
import api from '../../utils/api';
import { PERMISSIONS, buildRoleFeatureMatrix, canFeatureForRole } from '../../utils/permissions';
import ConfirmActionModal from '../../components/modals/ConfirmActionModal';
import {
  ABANDONED_FEEDBACK_MARKER,
  isAbandonedSupervisorRatingEntry,
} from '../../utils/matchFeedbackAbandoned';
import {
  formatMatchDayPillLabel,
  formatScheduledStartLabel,
  isTlFeedbackWindowOpen,
} from '../../utils/matchFeedbackSchedule';
import { formatDateTime } from '../../utils/feedbackViewFormat';
import { FEEDBACK_TABLE_HEAD_GRADIENT } from '../../utils/feedbackViewSharedUi';
import { isDaysMatchType, resolveFeedbackDayNumber as resolveDayNumber } from '../../utils/matchTypeUi';
import { getRoleIconComponent, TEAM_MEMBER_MODAL_ACCENT } from '../../utils/teamMemberDetailModalUi';

const getDaysDuration = (startDateValue, endDateValue) => {
  const start = new Date(startDateValue);
  const end = new Date(endDateValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;
  const oneDayMs = 24 * 60 * 60 * 1000;
  const utcStart = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const utcEnd = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  const diffDays = Math.floor((utcEnd - utcStart) / oneDayMs) + 1;
  if (!Number.isFinite(diffDays) || diffDays <= 0) return 1;
  return Math.max(1, diffDays);
};

const collectSupervisorRecords = (payload) => {
  const raw = payload?.data || payload;
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (Array.isArray(raw?.feedbacks)) return raw.feedbacks.filter(Boolean);
  if (Array.isArray(raw?.records)) return raw.records.filter(Boolean);
  if (Array.isArray(raw?.data)) return raw.data.filter(Boolean);
  if (raw && typeof raw === 'object') return [raw];
  return [];
};

const normalizeRatingValue = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '5.0';
  const clamped = Math.min(5, Math.max(0, parsed));
  return clamped.toFixed(1);
};

const sanitizeRatingInput = (rawValue) => {
  const text = String(rawValue ?? '').trim();
  if (text === '') return '';
  const normalizedText = text.replace(',', '.');
  if (!/^\d*\.?\d*$/.test(normalizedText)) return null;
  const parts = normalizedText.split('.');
  if (parts.length > 2) return null;
  const decimalPart = parts[1] ?? '';
  if (decimalPart.length > 1) return null;
  const numericValue = Number(normalizedText === '.' ? '0' : normalizedText);
  if (!Number.isFinite(numericValue)) return null;
  if (numericValue < 0 || numericValue > 5) return null;
  return normalizedText;
};

const SupervisorFeedbackPage = () => {
  const { matchId, eventTeamId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const appBasePath = location.pathname.startsWith('/employee') ? '/employee' : '/admin';

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [members, setMembers] = useState([]);   // [{ id, name, role, rating, remarks }]
  const [overallFeedback, setOverallFeedback] = useState('');
  const [submittedDays, setSubmittedDays] = useState([]);
  const [selectedFeedbackDay, setSelectedFeedbackDay] = useState(1);
  const [dayDrafts, setDayDrafts] = useState({});
  const [submitWarningOpen, setSubmitWarningOpen] = useState(false);
  const [submitIntent, setSubmitIntent] = useState('feedback');
  const [scheduleTick, setScheduleTick] = useState(0);

  const currentUser = (() => {
    try { return JSON.parse(sessionStorage.getItem('user') || '{}'); } catch { return {}; }
  })();
  const evaluatorName = currentUser?.name || currentUser?.fullName || currentUser?.email || '';
  const matchTypeFromState = location.state?.matchType;
  const isDaysMatch = useMemo(
    () => isDaysMatchType(matchTypeFromState ?? matchData?.matchType),
    [matchTypeFromState, matchData?.matchType]
  );
  const daysDuration = useMemo(() => {
    if (!isDaysMatch) return 1;
    return getDaysDuration(
      location.state?.startDate || matchData?.startDate || matchData?.date,
      location.state?.endDate || matchData?.endDate
    );
  }, [
    isDaysMatch,
    location.state?.startDate,
    location.state?.endDate,
    matchData?.startDate,
    matchData?.date,
    matchData?.endDate,
  ]);
  const dayOptions = useMemo(
    () => (isDaysMatch ? Array.from({ length: daysDuration }, (_, idx) => idx + 1) : [1]),
    [isDaysMatch, daysDuration]
  );

  useEffect(() => {
    if (!matchId || !eventTeamId) return;
    const load = async () => {
      setLoading(true);
      try {
        let matrix = {};
        try {
          const permResponse = await api.getPermissions();
          const permissionRows = Array.isArray(permResponse?.data)
            ? permResponse.data
            : Array.isArray(permResponse?.data?.permissions)
            ? permResponse.data.permissions
            : Array.isArray(permResponse?.permissions)
            ? permResponse.permissions
            : [];
          matrix = buildRoleFeatureMatrix(permissionRows);
        } catch {
          matrix = {};
        }
        if (!canFeatureForRole(currentUser?.role, matrix, [PERMISSIONS.GIVE_SUPERVISOR_FEEDBACK])) {
          toast.error('You do not have permission to give supervisor feedback');
          navigate(`${appBasePath}/matches`);
          setLoading(false);
          return;
        }

        const [matchesRes, teamRes] = await Promise.all([
          api.getMatches(),
          api.getEventTeamById(eventTeamId),
        ]);

        // Find the specific match
        const rows = Array.isArray(matchesRes?.data) ? matchesRes.data
          : Array.isArray(matchesRes) ? matchesRes : [];
        const match = rows.find((m) => String(m._id || m.id) === String(matchId));
        setMatchData(match || null);

        // Build member rows from event team
        const teamItem = teamRes?.data?.data || teamRes?.data || teamRes || {};
        const teamMembers = Array.isArray(teamItem?.teamMembers) ? teamItem.teamMembers
          : Array.isArray(teamItem?.members) ? teamItem.members : [];

        const defaultMembers = teamMembers.map((m, idx) => ({
          id: String(m.member?._id || m.member?.id || m.member || m.employee?._id || idx),
          name: m.member?.name || m.member?.fullName || m.employee?.name || m.employee?.fullName || '-',
          role: m.role?.name || m.role?.title || (typeof m.role === 'string' ? m.role : '-'),
          rating: '5.0',
          remarks: '',
        }));

        if (isDaysMatch) {
          let existingRecords = [];
          try {
            const feedbackRes = await api.getSupervisorFeedbackByMatchId(matchId);
            existingRecords = collectSupervisorRecords(feedbackRes).filter((record) => {
              const teamId = String(record?.eventTeamId?._id || record?.eventTeamId || '').trim();
              return !teamId || teamId === String(eventTeamId);
            });
          } catch (err) {
            if (err?.status !== 404) throw err;
          }

          const drafts = {};
          dayOptions.forEach((dayNo) => {
            const existingForDay = existingRecords.find((record) => resolveDayNumber(record) === dayNo);
            if (existingForDay && Array.isArray(existingForDay.ratings)) {
              const mapped = defaultMembers.map((member) => {
                const row = existingForDay.ratings.find(
                  (entry) => String(entry?.employeeId?._id || entry?.employeeId || '') === String(member.id)
                );
                return row
                  ? {
                      ...member,
                      rating: isAbandonedSupervisorRatingEntry(row)
                        ? ABANDONED_FEEDBACK_MARKER
                        : Number.isFinite(Number(row.rating))
                        ? normalizeRatingValue(row.rating)
                        : member.rating,
                      remarks: row.remarks || '',
                    }
                  : member;
              });
              drafts[dayNo] = {
                members: mapped,
                overallFeedback: existingForDay?.overallFeedback || '',
              };
            } else {
              drafts[dayNo] = {
                members: defaultMembers.map((m) => ({ ...m })),
                overallFeedback: '',
              };
            }
          });
          setDayDrafts(drafts);
          setSubmittedDays(
            Array.from(
              new Set(existingRecords.map((record) => resolveDayNumber(record)).filter(Boolean))
            ).sort((a, b) => a - b)
          );
          setMembers((drafts[1]?.members || defaultMembers).map((m) => ({ ...m })));
          setOverallFeedback(drafts[1]?.overallFeedback || '');
        } else {
          setMembers(defaultMembers);
        }
      } catch (err) {
        toast.error(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [matchId, eventTeamId, isDaysMatch, dayOptions, appBasePath, navigate]);

  useEffect(() => {
    if (!isDaysMatch) return;
    if (!dayOptions.includes(selectedFeedbackDay)) {
      setSelectedFeedbackDay(dayOptions[0] || 1);
    }
  }, [isDaysMatch, dayOptions, selectedFeedbackDay]);

  useEffect(() => {
    if (!isDaysMatch) return;
    const draft = dayDrafts[selectedFeedbackDay];
    if (!draft) return;
    setMembers((draft.members || []).map((m) => ({ ...m })));
    setOverallFeedback(draft.overallFeedback || '');
  }, [isDaysMatch, selectedFeedbackDay, dayDrafts]);

  useEffect(() => {
    if (!matchId) return undefined;
    const id = window.setInterval(() => setScheduleTick((x) => x + 1), 30000);
    return () => window.clearInterval(id);
  }, [matchId]);

  const scheduleStartDate =
    location.state?.startDate ||
    matchData?.startDate ||
    matchData?.date ||
    '';
  const scheduleEndDate =
    location.state?.endDate ||
    matchData?.endDate ||
    '';
  const scheduleEndTime =
    location.state?.endTime ||
    matchData?.endTime ||
    '';
  const scheduleStartTime =
    location.state?.startTime ||
    matchData?.startTime ||
    '';
  const teamNameLabel =
    location.state?.teamName ||
    location.state?.eventName ||
    location.state?.team?.teamName ||
    location.state?.team?.name ||
    matchData?.eventTeam?.teamName ||
    matchData?.eventTeam?.eventName ||
    matchData?.eventTeamId?.teamName ||
    matchData?.eventTeamId?.eventName ||
    '';
  const matchInfoItems = [
    { label: 'Match', value: matchData?.matchName || '-' },
    { label: 'Team', value: teamNameLabel || '-' },
    { label: 'Venue', value: matchData?.matchLocation || '-' },
    {
      label: 'Start Date & Time',
      value: formatDateTime(matchData?.startDate, matchData?.startTime) || '-',
    },
    {
      label: 'End Date & Time',
      value: formatDateTime(matchData?.endDate, matchData?.endTime) || '-',
    },
  ];

  void scheduleTick;

  const supervisorFeedbackScheduleOpen =
    !matchId
      ? true
      : isTlFeedbackWindowOpen({
          isDaysMatch,
          startDate: scheduleStartDate,
          startTime: scheduleStartTime,
          dayNumber: selectedFeedbackDay,
          now: new Date(),
        });

  const isScheduleBlocked = Boolean(matchId) && !supervisorFeedbackScheduleOpen;

  const updateMember = (id, field, value) => {
    setMembers((prev) =>
      prev.map((m) => m.id === id ? { ...m, [field]: value } : m)
    );
  };

  const handleSelectDay = (dayNo) => {
    setDayDrafts((prev) => ({
      ...prev,
      [selectedFeedbackDay]: {
        members: members.map((m) => ({ ...m })),
        overallFeedback,
      },
    }));
    setSelectedFeedbackDay(dayNo);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (matchId && !supervisorFeedbackScheduleOpen) {
      toast.error(
        isDaysMatch
          ? `Supervisor feedback opens when Day ${selectedFeedbackDay} starts (${formatScheduledStartLabel(true, scheduleStartDate, scheduleStartTime, selectedFeedbackDay) || '—'}).`
          : `Supervisor feedback opens when the match starts (${formatScheduledStartLabel(false, scheduleStartDate, scheduleStartTime, 1) || '—'}).`
      );
      return;
    }
    if (members.length === 0) {
      toast.error('No team members to submit feedback for');
      return;
    }
    setSubmitIntent('feedback');
    setSubmitWarningOpen(true);
  };

  const handleAbandonedSubmit = () => {
    if (submitting || isSelectedDaySubmitted) return;
    if (matchId && !supervisorFeedbackScheduleOpen) {
      toast.error(
        isDaysMatch
          ? `You can mark abandoned only after Day ${selectedFeedbackDay} has started.`
          : 'You can mark abandoned only after the scheduled match start.'
      );
      return;
    }
    if (members.length === 0) {
      toast.error('No team members to mark abandoned for');
      return;
    }
    setSubmitIntent('abandoned');
    setSubmitWarningOpen(true);
  };

  const handleConfirmSubmit = async () => {
    const isAbandonedIntent = submitIntent === 'abandoned';
    if (matchId && !supervisorFeedbackScheduleOpen) {
      toast.error('This match day has not reached its scheduled start yet.');
      setSubmitWarningOpen(false);
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        matchId,
        eventTeamId,
        ...(isDaysMatch
          ? {
              feedbackDay: selectedFeedbackDay,
              matchDay: selectedFeedbackDay,
              dayNumber: selectedFeedbackDay,
            }
          : {}),
        evaluator: evaluatorName,
        overallFeedback: isAbandonedIntent
          ? (overallFeedback?.trim() || 'Match abandoned')
          : overallFeedback,
        abandoned: isAbandonedIntent,
        isAbandoned: isAbandonedIntent,
        matchStatus: isAbandonedIntent ? 'abandoned' : undefined,
        feedback: members.map((m) => ({
          employeeId: m.id,
          role: m.role,
          rating: isAbandonedIntent ? ABANDONED_FEEDBACK_MARKER : Number(m.rating),
          remarks: isAbandonedIntent ? ABANDONED_FEEDBACK_MARKER : m.remarks,
        })),
      };

      // TODO: replace with actual endpoint when ready
      await api.submitSupervisorFeedback(payload);
      if (!isDaysMatch) {
        toast.success(isAbandonedIntent ? 'Match marked as abandoned' : 'Feedback submitted successfully');
        setSubmitWarningOpen(false);
        navigate(`${appBasePath}/matches`);
        return;
      }

      const nextSubmitted = Array.from(new Set([...submittedDays, selectedFeedbackDay])).sort((a, b) => a - b);
      setSubmittedDays(nextSubmitted);
      toast.success(
        isAbandonedIntent
          ? `Day ${selectedFeedbackDay} marked as abandoned`
          : `Day ${selectedFeedbackDay} supervisor feedback submitted successfully`
      );
      const nextPending = dayOptions.find((day) => !nextSubmitted.includes(day));
      if (nextPending) {
        setDayDrafts((prev) => ({
          ...prev,
          [selectedFeedbackDay]: {
            members: members.map((m) => ({ ...m })),
            overallFeedback,
          },
        }));
        setSelectedFeedbackDay(nextPending);
      } else {
        navigate(`${appBasePath}/matches`);
      }
      setSubmitWarningOpen(false);
    } catch (err) {
      toast.error(err.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const isSelectedDaySubmitted =
    isDaysMatch && submittedDays.includes(selectedFeedbackDay);
  const isSelectedDayReadOnly = isSelectedDaySubmitted || isScheduleBlocked;

  const getDayPillStyle = (dayNumber) => {
    const isSubmitted = submittedDays.includes(dayNumber);
    const isSelected = selectedFeedbackDay === dayNumber;

    if (isSubmitted && isSelected) {
      return {
        background: '#16a34a',
        border: '1px solid #16a34a',
        color: 'white',
      };
    }

    if (isSubmitted) {
      return {
        background: '#dcfce7',
        border: '1px solid #22c55e',
        color: '#166534',
      };
    }

    return {
      background: isSelected ? '#ef4444' : '#fee2e2',
      border: isSelected ? '1px solid #ef4444' : '1px solid #f87171',
      color: isSelected ? 'white' : '#b91c1c',
    };
  };

  const quickSummary = useMemo(() => {
    if (!members.length) return null;
    const numericRatings = members
      .map((member) => Number(member?.rating))
      .filter((value) => Number.isFinite(value));
    if (!numericRatings.length) return null;
    const total = numericRatings.reduce((sum, value) => sum + value, 0);
    return total / numericRatings.length;
  }, [members]);

  return (
    <div style={{ padding: '16px' }}>
      <ConfirmActionModal
        isOpen={submitWarningOpen}
        onClose={() => {
          if (!submitting) setSubmitWarningOpen(false);
        }}
        onConfirm={handleConfirmSubmit}
        title={submitIntent === 'abandoned' ? 'Mark match as abandoned?' : 'Submit supervisor feedback?'}
        message={
          submitIntent === 'abandoned'
            ? 'This will mark the selected match/day as abandoned and no supervisor ratings will be saved. This action is final and cannot be changed.'
            : 'Supervisor feedback can only be submitted once for this day. After submit, you cannot edit or resubmit. Please confirm.'
        }
        confirmText={submitIntent === 'abandoned' ? 'Yes, mark abandoned' : 'Yes, submit once'}
        cancelText="Go back and review"
        confirmColor={submitIntent === 'abandoned' ? '#dc2626' : '#2563eb'}
        loading={submitting}
      />
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '16px' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <div>
            <h2 style={{ margin: '4px 0', color: '#101828' }}>Supervisor Feedback</h2>
            {/* {matchData && (
              <p style={{ margin: 0, color: '#667085', fontSize: '13px' }}>
                {matchData.matchName} — {matchData.matchLocation}
              </p>
            )} */}
          </div>
          <button
            type="button"
            onClick={() => navigate(`${appBasePath}/matches`)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '34px',
              padding: '0 14px',
              border: '1px solid #d0d5dd',
              background: '#ffffff',
              color: '#1e293b',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            <FiArrowLeft size={14} strokeWidth={2.2} aria-hidden />
            Back
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#98a2b3', fontSize: '13px', padding: '40px 0' }}>Loading...</div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '16px' }}>

            {/* Match info strip */}
            {matchData && (
              <div style={{ padding: '12px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', columnGap: '16px', rowGap: '0px', alignItems: 'start' }}>
                {matchInfoItems.map((item) => (
                  <div key={item.label}>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px', fontWeight: 500 }}>{item.label}</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{item.value}</div>
                  </div>
                ))}
                </div>
              </div>
            )}
            {isScheduleBlocked && (
              <div
                role="status"
                style={{
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: '1px solid #fcd34d',
                  background: '#fffbeb',
                  color: '#92400e',
                  fontSize: '13px',
                  lineHeight: 1.5,
                }}
              >
                {isDaysMatch
                  ? `Supervisor feedback for Day ${selectedFeedbackDay} opens when that day starts (${formatScheduledStartLabel(true, scheduleStartDate, scheduleStartTime, selectedFeedbackDay) || '—'}).`
                  : `Supervisor feedback opens when the match starts (${formatScheduledStartLabel(false, scheduleStartDate, scheduleStartTime, 1) || '—'}).`}
              </div>
            )}
            {isDaysMatch && (
              <div>
                <label style={{ display: 'block', fontSize: '11px', color: '#667085', fontWeight: 700, marginBottom: '6px' }}>
                  MATCH DAY
                </label>
                <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '8px', overflowX: 'auto', paddingBottom: '2px' }}>
                  {dayOptions.map((dayNo) => {
                    const pillStyle = getDayPillStyle(dayNo);
                    return (
                      <button
                        key={dayNo}
                        type="button"
                        onClick={() => handleSelectDay(dayNo)}
                        disabled={submitting}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '999px',
                          ...pillStyle,
                          cursor: submitting ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          opacity: submitting ? 0.7 : 1,
                        }}
                      >
                        {formatMatchDayPillLabel(dayNo, scheduleStartDate)}
                      </button>
                    );
                  })}
                </div>
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: '2px' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#475467' }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '999px', background: '#dcfce7', border: '1px solid #22c55e' }} />
                    Submitted
                  </div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#475467' }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '999px', background: '#fee2e2', border: '1px solid #f87171' }} />
                    Pending
                  </div>
                </div>
              </div>
            )}

            {/* Members table */}
            {members.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#98a2b3', fontSize: '13px', padding: '32px 0' }}>
                No team members found. Please assign a team to this match first.
              </div>
            ) : (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={FEEDBACK_TABLE_HEAD_GRADIENT}>
                    <tr>
                      <th style={{ padding: '12px 14px', textAlign: 'center', fontSize: '12px', color: '#f8fafc', fontWeight: 700, borderBottom: '1px solid rgba(255, 255, 255, 0.2)', width: '52px' }}>S.No</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '12px', color: '#f8fafc', fontWeight: 700, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>Job Type</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '12px', color: '#f8fafc', fontWeight: 700, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>Member</th>
                      <th style={{ padding: '12px 14px', textAlign: 'center', fontSize: '12px', color: '#f8fafc', fontWeight: 700, borderBottom: '1px solid rgba(255, 255, 255, 0.2)', width: '160px' }}>Rating (0.0–5.0)</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '12px', color: '#f8fafc', fontWeight: 700, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m, idx) => (
                      <tr key={m.id} style={{ borderTop: '1px solid #e5e7eb', background: '#ffffff' }}>
                        <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: '13px', color: '#64748b', fontWeight: 600 }}>{idx + 1}</td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#334155', verticalAlign: 'middle' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                            {(() => {
                              const RoleIcon = getRoleIconComponent(m.role);
                              return <RoleIcon size={17} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />;
                            })()}
                            {m.role}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#334155', fontWeight: 600, verticalAlign: 'middle' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                            <FiUser size={17} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                            {m.name}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <button
                              type="button"
                              disabled={isSelectedDayReadOnly}
                              onClick={() => {
                                const current = parseFloat(m.rating);
                                const next = Math.max(0, Math.round(((Number.isNaN(current) ? 5 : current) - 0.1) * 10) / 10);
                                updateMember(m.id, 'rating', normalizeRatingValue(next));
                              }}
                              style={{ width: '26px', height: '26px', border: '1px solid #d0d5dd', borderRadius: '6px', background: '#f9fafb', cursor: isSelectedDayReadOnly ? 'not-allowed' : 'pointer', fontSize: '15px', fontWeight: 700, color: '#344054', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: isSelectedDayReadOnly ? 0.65 : 1 }}
                            >−</button>
                            <input
                              type="text"
                              value={m.rating}
                              disabled={isSelectedDayReadOnly}
                              inputMode="decimal"
                              onChange={(e) => {
                                const nextValue = sanitizeRatingInput(e.target.value);
                                if (nextValue !== null) updateMember(m.id, 'rating', nextValue);
                              }}
                              onBlur={(e) => {
                                updateMember(m.id, 'rating', normalizeRatingValue(e.target.value));
                              }}
                              style={{ width: '58px', padding: '4px 6px', border: '1px solid #d0d5dd', borderRadius: '6px', fontSize: '13px', textAlign: 'center', fontWeight: 600 }}
                            />
                            <button
                              type="button"
                              disabled={isSelectedDayReadOnly}
                              onClick={() => {
                                const current = parseFloat(m.rating);
                                const next = Math.min(5, Math.round(((Number.isNaN(current) ? 5 : current) + 0.1) * 10) / 10);
                                updateMember(m.id, 'rating', normalizeRatingValue(next));
                              }}
                              style={{ width: '26px', height: '26px', border: '1px solid #d0d5dd', borderRadius: '6px', background: '#f9fafb', cursor: isSelectedDayReadOnly ? 'not-allowed' : 'pointer', fontSize: '15px', fontWeight: 700, color: '#344054', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: isSelectedDayReadOnly ? 0.65 : 1 }}
                            >+</button>
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <input
                            type="text"
                            value={m.remarks}
                            disabled={isSelectedDayReadOnly}
                            onChange={(e) => updateMember(m.id, 'remarks', e.target.value)}
                            placeholder="Add remarks..."
                            style={{ width: '100%', padding: '6px 10px', border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Overall feedback + quick summary */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.9fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#475467', fontWeight: 600, marginBottom: '6px' }}>Overall Feedback</label>
                <textarea
                  rows={3}
                  value={overallFeedback}
                  disabled={isSelectedDayReadOnly}
                  onChange={(e) => setOverallFeedback(e.target.value)}
                  placeholder="Enter overall supervisor feedback..."
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d5dd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ display: 'grid', gap: '10px' }}>
                <div style={{ border: '1px dashed #d0d5dd', borderRadius: '12px', padding: '12px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: '#667085', fontWeight: 700 }}>QUICK SUMMARY</div>
                    <div style={{ marginTop: '4px', fontSize: '38px', lineHeight: 1, fontWeight: 700, color: '#101828' }}>
                      {quickSummary == null ? 'N/A' : quickSummary.toFixed(1)}
                    </div>
                    <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                      <div style={{ fontSize: '12px', color: '#667085' }}>
                        Average
                      </div>
                      {evaluatorName ? (
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '11px', color: '#667085', fontWeight: 700 }}>EVALUATOR</div>
                          <div style={{ marginTop: '2px', fontSize: '13px', color: '#111827', fontWeight: 600 }}>{evaluatorName}</div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              
              <button
                type="button"
                onClick={handleAbandonedSubmit}
                disabled={submitting || members.length === 0 || isSelectedDaySubmitted || isScheduleBlocked}
                style={{
                  padding: '8px 16px',
                  border: isSelectedDaySubmitted ? '1px solid #d0d5dd' : '1px solid #fecaca',
                  background: isSelectedDaySubmitted ? '#f2f4f7' : '#fff1f2',
                  color: isSelectedDaySubmitted ? '#667085' : '#b91c1c',
                  borderRadius: '8px',
                  cursor: (submitting || members.length === 0 || isSelectedDaySubmitted || isScheduleBlocked) ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  opacity: (submitting || members.length === 0 || isSelectedDaySubmitted || isScheduleBlocked) ? 0.7 : 1,
                }}
              >
                {isSelectedDaySubmitted
                  ? 'Abandoned'
                  : submitting
                  ? 'Submitting...'
                  : isDaysMatch
                  ? `Submit Day ${selectedFeedbackDay} as Abandoned`
                  : 'Submit as Abandoned'}
              </button>
              <button
                type="submit"
                disabled={submitting || members.length === 0 || isSelectedDaySubmitted || isScheduleBlocked}
                style={{
                  padding: '8px 16px',
                  border: isSelectedDaySubmitted ? '1px solid #d0d5dd' : 'none',
                  background: isSelectedDaySubmitted
                    ? '#f2f4f7'
                    : 'linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)',
                  color: isSelectedDaySubmitted ? '#667085' : 'white',
                  borderRadius: '8px',
                  cursor: (submitting || members.length === 0 || isSelectedDaySubmitted || isScheduleBlocked) ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  opacity: (submitting || members.length === 0 || isSelectedDaySubmitted || isScheduleBlocked) ? 0.7 : 1,
                }}
              >
                {isSelectedDaySubmitted
                  ? 'Submitted'
                  : submitting
                  ? 'Submitting...'
                  : isDaysMatch
                  ? `Submit Day ${selectedFeedbackDay}`
                  : 'Submit '}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SupervisorFeedbackPage;
